import { useState, useEffect } from "react";
import { View, Text, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, Image, TouchableOpacity } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import { spacing, typography } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { Ionicons } from "@expo/vector-icons";

import { AuthForm } from "../../components/auth/AuthForm";
import { Card } from "../../components/ui/Card";
import { AppDownloadBanner } from "../../components/AppDownloadBanner";

export default function AuthScreen() {
  const { t } = useTranslation();
  const colors = useColors();
  const insets = useSafeAreaInsets();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const navigation = useNavigation();

  const styles = createStyles(colors);

  useEffect(() => {
    navigation.setOptions({
      headerShown: false,
    });
  }, [navigation]);

  const toggleMode = () => {
    const newMode = mode === "signin" ? "signup" : "signin";
    setMode(newMode);
  };

  const handleForgotPassword = () => {
    navigation.navigate("ForgotPassword" as never);
  };

  const goToPrivacy = () => navigation.navigate("PrivacyPolicy" as never);
  const goToTerms = () => navigation.navigate("TermsAndConditions" as never);

  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      // Fallback if no history
      navigation.navigate("Landing" as never);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.keyboardContainer}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? insets.top : 0}
    >
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <AppDownloadBanner 
          compact 
          style={styles.topBanner} 
        />
        <TouchableOpacity 
          onPress={handleBack} 
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>

        <View style={styles.container}>
          <View style={styles.content}>
            <View style={styles.logoContainer}>
              <Image 
                source={require("../../assets/logo.png")} 
                style={styles.logoImage}
                resizeMode="contain"
              />
            </View>
            <View style={styles.welcomeTextContainer}>
              <Text style={styles.title}>Welcome</Text>
              <Text style={styles.subtitle}>Your Style Awaits</Text>
            </View>
            <Card>
              <AuthForm
                mode={mode}
                onNavigateToTerms={goToTerms}
                onNavigateToPrivacy={goToPrivacy}
              />
              <Text style={styles.toggle} onPress={toggleMode}>
                {mode === "signin"
                  ? t("auth.needAccount")
                  : t("auth.alreadyHaveAccount")}
              </Text>
              <Text style={styles.forgotPassword} onPress={handleForgotPassword}>
                {t("auth.forgotPassword")}
              </Text>
            </Card>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    keyboardContainer: {
      flex: 1,
      backgroundColor: colors.background,
    },
    scrollView: {
      backgroundColor: colors.background,
    },
    scrollContent: {
      flexGrow: 1,
      paddingTop: spacing.sm,
      paddingBottom: spacing.md,
    },
    topBanner: {
      paddingHorizontal: spacing.md,
      marginBottom: spacing.xs,
    },
    backButton: {
      position: 'absolute',
      top: spacing.sm,
      left: spacing.md,
      zIndex: 100,
      padding: spacing.xs,
    },
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    content: {
      flex: 1,
      padding: spacing.md,
      paddingTop: spacing.sm,
      justifyContent: "flex-start",
    },
    title: {
      ...typography.title,
      fontSize: 28,
      fontWeight: "800",
      textAlign: "center",
      marginBottom: spacing.xs,
      color: colors.text,
      letterSpacing: -0.5,
    },
    subtitle: {
      ...typography.body,
      fontSize: 14,
      textAlign: "center",
      color: colors.textLight,
      fontWeight: "500",
    },
    toggle: {
      marginTop: spacing.sm,
      textAlign: "center",
      color: colors.primary,
      fontSize: 14,
    },
    forgotPassword: {
      color: colors.primary,
      marginTop: spacing.sm,
      textAlign: "center",
      fontSize: 14,
    },
    logoContainer: {
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "center",
      marginBottom: spacing.md,
    },
    logoImage: {
      width: 80,
      height: 80,
      borderRadius: 20,
      overflow: 'hidden',
    },
    welcomeTextContainer: {
      alignItems: "center",
      marginBottom: spacing.md,
    },
  });
